import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Category, Budget } from '../types';
import api from '../services/api';

const BudgetManagement: React.FC = () => {
  const { t } = useTranslation();
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [categories, setCategories] = useState<Category[]>([]);
  const [budgets, setBudgets] = useState<{ [key: string]: number }>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const months = [
    { number: 1, name: t('budget:monthShort1') },
    { number: 2, name: t('budget:monthShort2') },
    { number: 3, name: t('budget:monthShort3') },
    { number: 4, name: t('budget:monthShort4') },
    { number: 5, name: t('budget:monthShort5') },
    { number: 6, name: t('budget:monthShort6') },
    { number: 7, name: t('budget:monthShort7') },
    { number: 8, name: t('budget:monthShort8') },
    { number: 9, name: t('budget:monthShort9') },
    { number: 10, name: t('budget:monthShort10') },
    { number: 11, name: t('budget:monthShort11') },
    { number: 12, name: t('budget:monthShort12') },
  ];

  const fetchCategories = useCallback(async () => {
    try {
      const response = await api.get('/categories');
      const data = response.data;
      // 精算用カテゴリを除外
      const filteredCategories = data.filter((cat: Category) => !cat.settlementFlag);
      setCategories(filteredCategories);
    } catch (error) {
      console.error(t('budget:fetchCategoriesError'), error);
    }
  }, []);

  const fetchBudgets = useCallback(async () => {
    if (!selectedYear) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/budgets/${selectedYear}`);
      const data: Budget[] = response.data;
      const budgetMap: { [key: string]: number } = {};
      data.forEach(budget => {
        const key = `${budget.categoryId}-${budget.month}`;
        budgetMap[key] = budget.amount;
      });
      setBudgets(budgetMap);
    } catch (error) {
      console.error(t('budget:fetchBudgetsError'), error);
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchBudgets();
  }, [fetchBudgets]);

  const handleBudgetChange = (categoryId: string, month: number, value: string) => {
    const key = `${categoryId}-${month}`;
    const amount = value === '' ? 0 : parseFloat(value) || 0;
    setBudgets(prev => ({ ...prev, [key]: amount }));
  };

  const saveBudgets = async () => {
    setSaving(true);
    try {
      const promises: Promise<any>[] = [];
      
      Object.entries(budgets).forEach(([key, amount]) => {
        const [categoryId, monthStr] = key.split('-');
        const month = parseInt(monthStr);
        
        if (amount > 0) {
          promises.push(
            api.post('/budgets', {
              year: selectedYear,
              month,
              categoryId,
              amount,
            })
          );
        } else {
          // 金額が0の場合は削除
          promises.push(
            api.delete(`/budgets/${selectedYear}/${month}/${categoryId}`)
          );
        }
      });

      await Promise.all(promises);
      alert(t('budget:budgetSavedSuccessfully'));
    } catch (error) {
      console.error('予算保存エラー:', error);
      alert(t('budget:budgetSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const getBudgetValue = (categoryId: string, month: number): number => {
    const key = `${categoryId}-${month}`;
    return budgets[key] || 0;
  };

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
  };

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 2; i <= currentYear + 2; i++) {
      years.push(i);
    }
    return years;
  };

  if (loading) {
    return <div className="text-center p-4">{t('common:loading')}</div>;
  }

  return (
    <div className="form-container" style={{maxWidth: '100%'}}>
      <h2 style={{textAlign: 'center', marginBottom: '30px', color: '#2c3e50', fontSize: '24px', fontWeight: 'bold'}}>{t('budget:budgetManagement')}</h2>
      
      <div className="form-group">
        <label>
          {t('budget:selectYear')}
        </label>
        <select
          value={selectedYear}
          onChange={(e) => handleYearChange(parseInt(e.target.value))}
        >
          {generateYearOptions().map(year => (
            <option key={year} value={year}>{year}{t('common:year')}</option>
          ))}
        </select>
      </div>

      <div className="budget-table-container">
        <table className="budget-table">
          <thead>
            <tr>
              <th className="budget-table-header budget-table-category-header">
                {t('common:category')}
              </th>
              {months.map(month => (
                <th key={month.number} className="budget-table-header budget-table-month-header">
                  {month.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {categories.map(category => (
              <tr key={category.id} className="budget-table-row">
                <td className="budget-table-cell budget-table-category-cell">
                  <div className="budget-category-info">
                    <span className="budget-category-name">{category.name}</span>
                    <span className={`budget-category-type ${category.type}`}>
                      {category.type === 'income' ? t('common:income') : t('common:expense')}
                    </span>
                  </div>
                </td>
                {months.map(month => (
                  <td key={month.number} className="budget-table-cell budget-table-input-cell">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={getBudgetValue(category.id, month.number)}
                      onChange={(e) => handleBudgetChange(category.id, month.number, e.target.value)}
                      className="budget-input"
                      placeholder="0"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{marginTop: '30px', display: 'flex', justifyContent: 'flex-end'}}>
        <button
          onClick={saveBudgets}
          disabled={saving}
          className={`btn btn-primary`}
          style={{opacity: saving ? 0.5 : 1, cursor: saving ? 'not-allowed' : 'pointer'}}
        >
          {saving ? t('common:saving') : t('common:save')}
        </button>
      </div>
    </div>
  );
};

export default BudgetManagement;